import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { StatusBadge } from "@/components/status-badge";
import { FieldShell, useConfirm } from "./crm-ui";
import { useCrmText, type CrmTextKey } from "@/lib/crm/i18n";
import { optionLabel, type CrmEntity } from "@/lib/crm/fields";
import { crmFetch, crmKeys, plural, useCrmTimeline, type CrmActivityInput, type CrmError, type CrmTimelineItem } from "@/lib/crm/api";
import { formatDateTime, formatTime, normalize } from "@/lib/crm/view-engine";
import { queryClient } from "@/lib/queryClient";
import {
  BadgeCheck, CalendarDays, Ellipsis, FileSignature, History, Link2, Loader2, Mail, Pencil, Phone, Plus,
  RefreshCw, Search, SquareCheck, StickyNote, Trash2, X, ExternalLink, Download,
} from "lucide-react";

type ActivityType = CrmActivityInput["type"];

interface Composer {
  id?: string;
  /** Record the activity belongs to when editing one shown from a related record. */
  regardingEntity?: CrmEntity;
  regardingId?: string;
  type: ActivityType;
  subject: string;
  description: string;
  dueAt: string;
  startAt: string;
  endAt: string;
  location: string;
  isCompleted: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");
/** ISO → value for <input type="datetime-local"> (local time). */
const toLocalInput = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

function defaults(type: ActivityType): Composer {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setMinutes(0, 0, 0);
  nextHour.setHours(nextHour.getHours() + 1);
  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);
  return {
    type,
    subject: "",
    description: "",
    dueAt: type === "task" ? toLocalInput(tomorrow9.toISOString()) : type === "call" ? toLocalInput(now.toISOString()) : "",
    startAt: type === "appointment" ? toLocalInput(nextHour.toISOString()) : "",
    endAt: type === "appointment" ? toLocalInput(new Date(nextHour.getTime() + 3_600_000).toISOString()) : "",
    location: "",
    isCompleted: false,
  };
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  note: StickyNote, task: SquareCheck, appointment: CalendarDays, call: Phone, email: Mail,
  signature: FileSignature, signed: BadgeCheck, system: History,
};
const TINTS: Record<string, string> = {
  note: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
  task: "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
  appointment: "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300",
  call: "bg-teal-100 text-teal-800 dark:bg-teal-950/50 dark:text-teal-300",
  email: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300",
  signature: "bg-lime-100 text-lime-800 dark:bg-lime-950/50 dark:text-lime-300",
  signed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
  system: "bg-muted text-muted-foreground",
};

const ACTIVITY_KINDS = ["note", "task", "appointment", "call", "email"];
const DOC_KINDS = ["signature", "signed"];

const icsDate = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const icsText = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

function calendarLinks(item: CrmTimelineItem) {
  const start = item.startAt!;
  const end = item.endAt || new Date(new Date(start).getTime() + 3_600_000).toISOString();
  const subject = item.subject || "";
  const details = item.description || "";
  const location = item.location || "";
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(subject)}&dates=${icsDate(start)}/${icsDate(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
  const outlook = `https://outlook.office.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&subject=${encodeURIComponent(subject)}&startdt=${encodeURIComponent(new Date(start).toISOString())}&enddt=${encodeURIComponent(new Date(end).toISOString())}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//FillSign//CRM//PT", "CALSCALE:GREGORIAN", "BEGIN:VEVENT",
    `UID:${item.id}@fillsign.com.br`, `DTSTAMP:${icsDate(new Date().toISOString())}`, `DTSTART:${icsDate(start)}`, `DTEND:${icsDate(end)}`,
    `SUMMARY:${icsText(subject)}`, details ? `DESCRIPTION:${icsText(details)}` : "", location ? `LOCATION:${icsText(location)}` : "",
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
  return { google, outlook, ics };
}

function downloadIcs(item: CrmTimelineItem) {
  const { ics } = calendarLinks(item);
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(item.subject || "compromisso").replace(/[^\w\-]+/g, "_").slice(0, 60)}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}
function writePref(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

interface Props {
  entity: CrmEntity;
  recordId: string | null;
  readOnly: boolean;
  recordEmail?: string;
  onSendDocument: () => void;
}

export function CrmTimeline({ entity, recordId, readOnly, recordEmail, onSendDocument }: Props) {
  const { lang, tx } = useCrmText();
  const { toast } = useToast();
  const [confirmDialog, ask] = useConfirm();
  const { data, isLoading, isFetching, refetch } = useCrmTimeline(entity, recordId);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState<"desc" | "asc">(() => readPref("fs:crm:timeline:sort", "desc"));
  const [kinds, setKinds] = useState<{ activities: boolean; documents: boolean; system: boolean }>(() =>
    readPref("fs:crm:timeline:kinds", { activities: true, documents: true, system: true }));
  const [composer, setComposer] = useState<Composer | null>(null);
  const [composerErrors, setComposerErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const items = useMemo(() => {
    const q = normalize(search);
    const list = (data || []).filter((i) => {
      const group = ACTIVITY_KINDS.includes(i.kind) ? "activities" : DOC_KINDS.includes(i.kind) ? "documents" : "system";
      if (!kinds[group]) return false;
      if (!q) return true;
      return normalize([i.subject, i.description, i.byName, i.location].filter(Boolean).join(" ")).includes(q);
    });
    list.sort((a, b) => (Date.parse(a.at) - Date.parse(b.at)) * (sortDir === "asc" ? 1 : -1));
    return list;
  }, [data, search, kinds, sortDir]);

  const changeSort = (dir: "asc" | "desc") => { setSortDir(dir); writePref("fs:crm:timeline:sort", dir); };
  const toggleKind = (k: keyof typeof kinds) => {
    const next = { ...kinds, [k]: !kinds[k] };
    setKinds(next);
    writePref("fs:crm:timeline:kinds", next);
  };

  // A client and its leads share the timeline: refresh every open one.
  const refresh = () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === crmKeys.all[0] && q.queryKey[2] === "timeline" });

  const openComposer = (type: ActivityType) => {
    setComposerErrors({});
    setComposer(defaults(type));
  };

  /** Activities of a related record (client ↔ leads) are saved on that record. */
  const activityPath = (i: { regardingEntity?: CrmEntity | null; regardingId?: string | null }, activityId?: string) =>
    `/api/crm/${plural(i.regardingEntity || entity)}/${i.regardingId || recordId}/activities${activityId ? `/${activityId}` : ""}`;

  const editItem = (i: CrmTimelineItem) => {
    setComposerErrors({});
    setComposer({
      id: i.id,
      regardingEntity: i.regardingEntity || undefined,
      regardingId: i.regardingId || undefined,
      type: i.kind as ActivityType,
      subject: i.subject || "",
      description: i.description || "",
      dueAt: toLocalInput(i.dueAt),
      startAt: toLocalInput(i.startAt),
      endAt: toLocalInput(i.endAt),
      location: i.location || "",
      isCompleted: i.isCompleted,
    });
  };

  const toInput = (c: Composer): CrmActivityInput => ({
    type: c.type,
    subject: c.subject.trim() || null,
    description: c.description.trim() || null,
    dueAt: c.type === "task" || c.type === "call" ? fromLocalInput(c.dueAt) : null,
    startAt: c.type === "appointment" ? fromLocalInput(c.startAt) : null,
    endAt: c.type === "appointment" ? fromLocalInput(c.endAt) : null,
    location: c.type === "appointment" ? c.location.trim() || null : null,
    isCompleted: c.type === "task" ? c.isCompleted : null,
  });

  const save = async () => {
    if (!composer || !recordId) return;
    const errors: Record<string, string> = {};
    if (composer.type === "note" && !composer.subject.trim() && !composer.description.trim()) errors.description = tx("err_required");
    if (composer.type !== "note" && !composer.subject.trim()) errors.subject = tx("err_required");
    if (composer.type === "appointment" && !composer.startAt) errors.startAt = tx("err_required");
    if (composer.type === "appointment" && composer.startAt && composer.endAt && composer.endAt < composer.startAt) errors.endAt = tx("err_invalid");
    setComposerErrors(errors);
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      const path = activityPath(composer, composer.id);
      await crmFetch<CrmTimelineItem>(composer.id ? "PUT" : "POST", path, toInput(composer));
      setComposer(null);
      toast({ title: composer.type === "email" && !composer.id ? tx("emailLogged") : tx("activitySaved") });
      await refresh();
    } catch (e) {
      const err = e as CrmError;
      const fe = typeof err.fieldErrors === "function" ? err.fieldErrors() : {};
      if (Object.keys(fe).length) setComposerErrors(Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, tx(v === "required" ? "err_required" : "err_invalid")])));
      else toast({ title: tx("error"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (i: CrmTimelineItem) => {
    if (!recordId) return;
    try {
      await crmFetch("PUT", activityPath(i, i.id), {
        type: i.kind, subject: i.subject, description: i.description, dueAt: i.dueAt, startAt: i.startAt, endAt: i.endAt,
        location: i.location, isCompleted: !i.isCompleted,
      });
      await refresh();
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    }
  };

  const remove = async (i: CrmTimelineItem) => {
    const ok = await ask({ title: tx("confirmDeleteActivity"), confirm: tx("delete"), cancel: tx("cancel"), destructive: true });
    if (!ok) return;
    try {
      await crmFetch("DELETE", `/api/crm/activities/${i.id}`);
      toast({ title: tx("activityDeleted") });
      await refresh();
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    }
  };

  const mailto = (subject?: string | null, body?: string | null) =>
    recordEmail ? `mailto:${recordEmail}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(body || "")}` : null;

  const systemText = (i: CrmTimelineItem) => {
    const ch = i.changes || [];
    switch (i.operation) {
      case "create": return ch.some((c) => c.field === "leadId") ? tx("sys_fromLead") : tx("sys_create");
      case "activate": return tx("sys_activate");
      case "deactivate": return tx("sys_deactivate");
      case "accept": return tx("sys_accept");
      case "disqualify": return tx("sys_disqualify");
      case "stage": {
        const c = ch.find((x) => x.field === "stage");
        return tx("sys_stage", { from: optionLabel("stage", c?.old, lang) || "—", to: optionLabel("stage", c?.new, lang) || "—" });
      }
      default: return tx("sys_update");
    }
  };

  const disabledReason = !recordId ? tx("saveFirst") : null;

  return (
    <section className="rounded-lg border bg-card text-card-foreground" data-testid="timeline">
      {confirmDialog}
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h3 className="text-sm font-semibold">{tx("timeline")}</h3>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("searchTimeline")} onClick={() => setShowSearch((s) => !s)}
            disabled={!recordId} data-testid="timeline-search-toggle">
            <Search className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("new")} disabled={!recordId || readOnly} data-testid="timeline-add">
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {(["note", "task", "appointment", "call", "email"] as ActivityType[]).map((k) => {
                const Icon = ICONS[k];
                return (
                  <DropdownMenuItem key={k} onClick={() => openComposer(k)} data-testid={`timeline-add-${k}`}>
                    <Icon className="mr-2 h-4 w-4" /> {tx(`new_${k}` as CrmTextKey)}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onSendDocument} data-testid="timeline-add-signature">
                <FileSignature className="mr-2 h-4 w-4" /> {tx("new_signature")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("refresh")} onClick={() => refetch()} disabled={!recordId} data-testid="timeline-refresh">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("more")} disabled={!recordId} data-testid="timeline-more">
                <Ellipsis className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuRadioGroup value={sortDir} onValueChange={(v) => changeSort(v as "asc" | "desc")}>
                <DropdownMenuRadioItem value="desc" data-testid="timeline-sort-desc">{tx("sortNewest")}</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="asc" data-testid="timeline-sort-asc">{tx("sortOldest")}</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{tx("show")}</DropdownMenuLabel>
              <DropdownMenuCheckboxItem checked={kinds.activities} onCheckedChange={() => toggleKind("activities")}>{tx("showActivities")}</DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={kinds.documents} onCheckedChange={() => toggleKind("documents")}>{tx("showDocuments")}</DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={kinds.system} onCheckedChange={() => toggleKind("system")}>{tx("showSystem")}</DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-3 p-4">
        {disabledReason ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{disabledReason}</p>
        ) : (
          <>
            {showSearch && (
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("searchTimeline")} className="pl-8"
                  data-testid="timeline-search" />
              </div>
            )}

            {!composer && !readOnly && (
              <button type="button" onClick={() => openComposer("note")}
                className="w-full rounded-md border border-dashed px-3 py-2 text-left text-sm text-muted-foreground hover-elevate"
                data-testid="timeline-note-quick">
                {tx("enterNote")}
              </button>
            )}

            {composer && (
              <div className="space-y-3 rounded-md border bg-muted/30 p-3" data-testid="timeline-composer">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{composer.id ? tx("edit_activity") : tx(`new_${composer.type}` as CrmTextKey)}</p>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setComposer(null)} title={tx("close")}><X className="h-4 w-4" /></Button>
                </div>
                <FieldShell label={composer.type === "note" ? tx("title") : tx("subject")} required={composer.type !== "note"} error={composerErrors.subject}>
                  <Input value={composer.subject} maxLength={300} onChange={(e) => setComposer({ ...composer, subject: e.target.value })} data-testid="composer-subject" />
                </FieldShell>
                {(composer.type === "task" || composer.type === "call") && (
                  <FieldShell label={composer.type === "task" ? tx("dueAt") : tx("callAt")}>
                    <Input type="datetime-local" value={composer.dueAt} onChange={(e) => setComposer({ ...composer, dueAt: e.target.value })} data-testid="composer-due" />
                  </FieldShell>
                )}
                {composer.type === "appointment" && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FieldShell label={tx("startAt")} required error={composerErrors.startAt}>
                      <Input type="datetime-local" value={composer.startAt} onChange={(e) => {
                        const startAt = e.target.value;
                        // Keep the duration when the start moves.
                        let endAt = composer.endAt;
                        if (startAt && composer.startAt && composer.endAt) {
                          const dur = new Date(composer.endAt).getTime() - new Date(composer.startAt).getTime();
                          endAt = toLocalInput(new Date(new Date(startAt).getTime() + Math.max(dur, 0)).toISOString());
                        }
                        setComposer({ ...composer, startAt, endAt });
                      }} data-testid="composer-start" />
                    </FieldShell>
                    <FieldShell label={tx("endAt")} error={composerErrors.endAt}>
                      <Input type="datetime-local" value={composer.endAt} onChange={(e) => setComposer({ ...composer, endAt: e.target.value })} data-testid="composer-end" />
                    </FieldShell>
                    <FieldShell label={tx("location")} className="sm:col-span-2">
                      <Input value={composer.location} maxLength={300} onChange={(e) => setComposer({ ...composer, location: e.target.value })} data-testid="composer-location" />
                    </FieldShell>
                  </div>
                )}
                <FieldShell label={composer.type === "note" ? tx("noteText") : tx("description")} required={composer.type === "note"} error={composerErrors.description}>
                  <Textarea rows={composer.type === "note" ? 4 : 3} value={composer.description} maxLength={10000}
                    onChange={(e) => setComposer({ ...composer, description: e.target.value })} data-testid="composer-description" />
                </FieldShell>
                {composer.type === "task" && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={composer.isCompleted} onCheckedChange={(v) => setComposer({ ...composer, isCompleted: v === true })} />
                    {tx("markDone")}
                  </label>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {composer.type === "email" && mailto(composer.subject, composer.description) && (
                    <a className="mr-auto inline-flex items-center gap-1 text-xs underline underline-offset-2" href={mailto(composer.subject, composer.description)!}>
                      <Mail className="h-3.5 w-3.5" /> {tx("openMail")}
                    </a>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setComposer(null)} data-testid="composer-cancel">{tx("cancel")}</Button>
                  <Button size="sm" onClick={save} disabled={saving} data-testid="composer-save">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tx("save")}
                  </Button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center py-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : items.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{(data || []).length === 0 ? tx("emptyTimeline") : tx("noMatches")}</p>
            ) : (
              <ol className="space-y-2">
                {items.map((i) => {
                  const Icon = ICONS[i.kind] || History;
                  const isActivity = ACTIVITY_KINDS.includes(i.kind);
                  const overdue = i.kind === "task" && !i.isCompleted && i.dueAt && Date.parse(i.dueAt) < Date.now();
                  const title = i.kind === "system" ? systemText(i)
                    : i.kind === "signed" ? tx("signedBy", { name: i.byName || i.description || "" })
                    : i.subject || tx(`kind_${i.kind}` as CrmTextKey);
                  return (
                    <li key={i.id} className="group flex gap-3 rounded-md border bg-background p-3" data-testid="timeline-item" data-kind={i.kind}>
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${TINTS[i.kind] || TINTS.system}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                          <p className={`min-w-0 break-words text-sm font-medium ${i.kind === "task" && i.isCompleted ? "line-through text-muted-foreground" : ""}`}>
                            {i.kind === "signed" && <span className="font-normal text-muted-foreground">{i.subject} · </span>}
                            {title}
                          </p>
                          <span className="shrink-0 text-xs text-muted-foreground" title={formatDateTime(i.at, lang)}>{formatDateTime(i.at, lang)}</span>
                        </div>
                        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
                          <span>
                            {tx(`kind_${i.kind}` as CrmTextKey)}
                            {i.byName && i.kind !== "signed" ? ` · ${tx("by", { name: i.byName })}` : ""}
                          </span>
                          {i.regardingLabel && i.regardingId && i.regardingId !== recordId && (
                            <Link href={`/${i.regardingEntity === "lead" ? "leads" : "contacts"}/${i.regardingId}`}
                              className="inline-flex max-w-[16rem] items-center gap-1 rounded-full border px-2 py-px text-[11px] hover-elevate"
                              data-testid="timeline-related" title={tx("relatedOpen")}>
                              <Link2 className="h-3 w-3 shrink-0" />
                              <span className="truncate">{tx(i.regardingEntity === "lead" ? "relatedLead" : "relatedClient", { name: i.regardingLabel })}</span>
                            </Link>
                          )}
                        </p>

                        {i.kind === "signature" && (
                          <div className="flex flex-wrap items-center gap-2 pt-0.5">
                            {i.status && <StatusBadge status={i.status} />}
                            {i.description && <span className="text-xs text-muted-foreground">{tx("sentTo", { names: i.description })}</span>}
                          </div>
                        )}
                        {(i.kind === "task" || i.kind === "call") && i.dueAt && (
                          <p className={`text-xs ${overdue ? "font-medium text-destructive" : "text-muted-foreground"}`}>
                            {i.kind === "task" ? tx("dueAt") : tx("callAt")}: {formatDateTime(i.dueAt, lang)}{overdue ? ` · ${tx("overdue")}` : ""}
                          </p>
                        )}
                        {i.kind === "appointment" && i.startAt && (
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(i.startAt, lang)}{i.endAt ? ` – ${formatTime(i.endAt, lang)}` : ""}{i.location ? ` · ${i.location}` : ""}
                          </p>
                        )}
                        {isActivity && i.description && <p className="whitespace-pre-wrap break-words text-sm">{i.description}</p>}

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5 text-xs">
                          {i.kind === "task" && (
                            <label className="inline-flex cursor-pointer items-center gap-1.5">
                              <Checkbox checked={i.isCompleted} onCheckedChange={() => toggleComplete(i)} disabled={readOnly}
                                data-testid={`timeline-complete-${i.id}`} />
                              {i.isCompleted ? tx("completedTag") : tx("markDone")}
                            </label>
                          )}
                          {i.kind === "appointment" && i.startAt && (() => {
                            const links = calendarLinks(i);
                            return (
                              <>
                                <a className="inline-flex items-center gap-1 underline underline-offset-2" href={links.google} target="_blank" rel="noreferrer">
                                  {tx("googleCalendar")} <ExternalLink className="h-3 w-3" />
                                </a>
                                <a className="inline-flex items-center gap-1 underline underline-offset-2" href={links.outlook} target="_blank" rel="noreferrer">
                                  {tx("outlookCalendar")} <ExternalLink className="h-3 w-3" />
                                </a>
                                <button type="button" className="inline-flex items-center gap-1 underline underline-offset-2" onClick={() => downloadIcs(i)}>
                                  <Download className="h-3 w-3" /> {tx("downloadIcs")}
                                </button>
                              </>
                            );
                          })()}
                          {i.kind === "email" && mailto(i.subject, i.description) && (
                            <a className="inline-flex items-center gap-1 underline underline-offset-2" href={mailto(i.subject, i.description)!}>
                              <Mail className="h-3 w-3" /> {tx("openMail")}
                            </a>
                          )}
                          {i.documentId && (
                            <Link href={`/documents/${i.documentId}`} className="inline-flex items-center gap-1 underline underline-offset-2">
                              {tx("openDocument")}
                            </Link>
                          )}
                          {isActivity && i.editable && !readOnly && (
                            <span className="ml-auto flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("edit_activity")} onClick={() => editItem(i)} data-testid={`timeline-edit-${i.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("delete")} onClick={() => remove(i)} data-testid={`timeline-delete-${i.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}
      </div>
    </section>
  );
}
